import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core'
import { UtilityService } from '@common/services'
import { sideNavItems, sideNavSections } from '@modules/navigation/data/side-nav-dashboard.data'
import { NavigationService } from '@modules/navigation/services'
import { Subscription } from 'rxjs'
import { AccountService } from '@services/account.service'
import { SideNavItem, SideNavSectionItem } from '@modules/navigation/models'
import { Events } from '@services/events'
import { SupplierValidationService } from '@services/supplier-validation.service'
import { PlatformService } from '@services/platform.service'

@Component({
	selector: 'sbpro-layout-dashboard',
	templateUrl: './layout-dashboard.component.html',
	changeDetection: ChangeDetectionStrategy.Default,
	styleUrls: ['layout-dashboard.component.scss'],
	standalone: false
})
export class LayoutDashboardComponent implements OnInit, OnDestroy {
	@Input() static = false
	@Input() light = false

	@Input() supplier = false
	@Input() multitenant = false

	@HostBinding('class.sidenav-toggled') sideNavHidden = true

	sidenavStyle = 'sidenav-dark'
	/** Dentro do app nativo (spec 058): sem menu lateral nem rodapé, com o menu inferior. */
	appNative = false

	subscription: Subscription = new Subscription()

	_sideNavItems = sideNavItems
	_sideNavSections = sideNavSections

	constructor(
		private events: Events,
		public utilityService: UtilityService,
		public accountService: AccountService,
		public navigationService: NavigationService,
		private changeDetectorRef: ChangeDetectorRef,
		private supplierValidation: SupplierValidationService,
		private platform: PlatformService
	) {}

	ngOnInit() {
		if (this.light) {
			this.sidenavStyle = 'sidenav-light'
		}

		this.subscription.add(
			this.platform.mobile$.subscribe(mobile => {
				this.appNative = mobile
				this.changeDetectorRef.markForCheck()
			})
		)

		this.subscription.add(
			this.navigationService.sideNavVisible$().subscribe(isVisible => {
				this.sideNavHidden = !isVisible
				this.changeDetectorRef.markForCheck()
			})
		)

		this.events.on('reloadMenu').subscribe(() => {
			if (!this.multitenant) {
				this.loadMenu()
			}
		})

		// Quem é pessoa física só se sabe depois da leitura da sessão de validação do fornecedor.
		this.subscription.add(
			this.supplierValidation.changes$.subscribe(() => {
				if (!this.multitenant) {
					this.loadMenu()
					this.changeDetectorRef.markForCheck()
				}
			})
		)

		if (!this.multitenant) {
			this.loadMenu()
		}
	}

	ngOnDestroy() {
		this.subscription.unsubscribe()
	}

	closeSideNavIfOpen() {
		const BOOTSTRAP_LG_WIDTH = 992

		if (this.utilityService.window.innerWidth >= BOOTSTRAP_LG_WIDTH) {
			return
		}

		if (this.sideNavHidden) {
			this.navigationService.openedSideNav()
		}
	}

	private loadMenu() {
		for (const section of this._sideNavSections) {
			for (const item of section.items) {
				this.aclSideNavSections(item)

				const sideNavItem = this._sideNavItems[item.sideNavItemName]

				if (sideNavItem) {
					this.aclSideNavItems(sideNavItem)

					if (sideNavItem.link && sideNavItem.client) {
						sideNavItem.link = `${this.pathBase}/${sideNavItem.link}`
					}

					if (sideNavItem && sideNavItem.client) {
						this.addDomain(sideNavItem)
					}
				}
			}
		}
	}

	private aclSideNavSections(item: SideNavSectionItem) {
		item.active =
			item.products.includes(this.accountService.product.type) && item.roles.some((value: string) => this.accountService.allRoles.includes(value))
	}

	private aclSideNavItems(item: SideNavItem) {
		if (!item?.products && !item?.roles) {
			item.active = true
		} else {
			if (item.products) {
				item.active = item.products.includes(this.accountService.product.type)
			}
			if (item.roles) {
				item.active = item.roles.some(value => this.accountService.allRoles.includes(value))
			}
		}

		if (item.corporateSupplierOnly && this.supplierValidation.individual) {
			item.active = false
		}
	}

	private addDomain(sideNavItem: SideNavItem) {
		this.aclSideNavItems(sideNavItem)

		if (sideNavItem.submenu) {
			for (const sub of sideNavItem.submenu) {
				this.aclSideNavItems(sub)

				if (sub.link) {
					if (!sub.origin) {
						sub.origin = sub.link
					}

					const link = sub.origin
						?.replace(`${this.pathBase}/${this.domain}`, '')
						?.replace(`${this.pathBase}/`, '')
						?.replace(`/${this.domain}`, '')
						?.replace(`/default`, '')

					sub.link = `${this.pathBase}/${this.domain}${link}`
				}

				if (sub.submenu) {
					for (const subItem of sub.submenu) {
						this.addDomain(subItem)
						this.aclSideNavItems(subItem)
					}
				}
			}
		} else {
			if (sideNavItem.link) {
				if (!sideNavItem.origin) {
					sideNavItem.origin = sideNavItem.link
				}

				const link = sideNavItem.origin
					?.replace(`${this.pathBase}/${this.domain}`, '')
					?.replace(`${this.pathBase}/`, '')
					?.replace(`/${this.domain}`, '')
					?.replace(`/default`, '')

				sideNavItem.link = `${this.pathBase}/${this.domain}${link}`
			}
		}
	}

	private get domain() {
		return this.accountService.tenantName
	}

	get pathBase() {
		return '/org'
	}
}
